/* global window */

import $ from 'jquery';
import Masonry from 'masonry-layout';
import debounce from 'lodash/debounce';

import './GridArticles.scss';
import template from './GridArticles.vhtml';

var currentFactBox = 0;
var currentLanguage = window.siteData.language || 'da';

export default {
	name: 'GridArticles',
	template: template,
	props: {
		category: {
			type: [Number, String],
			default: '',
		},
		postTypes: {
			type: String,
			default: 'inspiration',
		},
		factBoxes: {
			type: Array,
			default: () => [],
		},
		highlightArticles: {
			type: Array,
			default: () => [],
		},
		articlesPerPage: {
			type: Number,
			default: 9,
		},
	},
	data: function () {
		return {
			articles: [],
			sortedArticles: [],
			masonry: null,
			offset: 0,
			isLoading: false,
			isChanging: false,
			showMore: true,
			currentFactBox: 0,
			videoData: {
				videoId: '',
				videoLength: '',
				isVertical: false,
				thumbnail: {
					lg: '',
					md: '',
					sm: '',
				},
			},
			showVideoPopup: false,
		};
	},
	watch: {
		category() {
			this.offset = 0;
			this.reload();
		},
		articles(updated) {
			const catString = String(this.category);
			const catID = parseInt(catString.split(',')[0]);
			let firstItems = [];
			let rest = [];
			updated.forEach((item) => {
				if (item.article_category && item.article_category.id === catID) {
					firstItems.push(item);
				} else {
					rest.push(item);
				}
			});

			this.sortedArticles = [...firstItems, ...rest];
		},
	},
	methods: {
		getFactBox(i) {
			if (this.factBoxes.length === 0) {
				return [];
			}

			if (this.showFactBox(i)) {
				const box = this.factBoxes[currentFactBox % this.factBoxes.length];
				currentFactBox++;
				return [box];
			} else {
				return [];
			}
		},
		showFactBox(i) {
			return i % 5 === 4 || i % 9 === 8 || i % 7 === 6;
		},
		getSize(i) {
			return i % 2;
		},
		getClass(i) {
			const classes = ['lg', 'md', 'sm'];
			const myClass = classes[this.getSize(i)];
			return 'article-card--' + myClass;
		},
		getImage(article, i) {
			const properties = ['lg', 'md', 'sm'];
			const property = properties[this.getSize(i)];
			return article.img && article.img[property] ? article.img[property] : '';
		},
		getYoutubeThumbnail(youtubeId) {
			return 'https://img.youtube.com/vi/' + youtubeId + '/maxresdefault.jpg';
		},
		handleVideoPopup(videoId, videoLength, thumbnail, isVertical) {
			if (this.postTypes != 'video') {
				return;
			}

			this.showVideoPopup = !this.showVideoPopup;
			document.body.style.overflow = this.showVideoPopup ? 'hidden' : 'auto';

			this.videoData.videoId = videoId;
			this.videoData.videoLength = videoLength;
			this.videoData.isVertical = isVertical;
			if (!thumbnail) {
				thumbnail = this.getYoutubeThumbnail(videoId);
			}
			this.videoData.thumbnail.lg = thumbnail;
			this.videoData.thumbnail.md = thumbnail;
			this.videoData.thumbnail.sm = thumbnail;
		},
		addArticles(articles, overwrite = false) {
			// We use the +one method to determine if there are more articles to show
			if (articles.length > this.articlesPerPage) {
				this.showMore = true;
				articles = articles.slice(0, this.articlesPerPage);
			} else {
				this.showMore = false;
			}
			if (overwrite) {
				this.articles = articles;
			} else {
				this.articles = [...this.articles, ...articles];
			}

			this.$nextTick(() => {
				if (this.masonry) {
					this.masonry.reloadItems();
					this.masonry.layout();
				} else {
					this.masonry = new Masonry(
						this.$el.querySelector('.js-article-grid'),
						{
							itemSelector: '.js-article-item',
						}
					);
				}
			});
		},
		fetchMore: debounce(function () {
			this.isLoading = true;
			//this.offset = this.offset + this.articlesPerPage;
			getArticles(
				this.postTypes,
				this.category,
				this.offset,
				this.highlightArticles,
				this.articlesPerPage + 1
			)
				.then((res) => {
					if (res) {
						if (typeof res != 'object') {
							res = JSON.parse(res);
						}

						this.offset = res.offset - 1;
						this.addArticles(res.data);
					}
				})
				.always(() => (this.isLoading = false));
		}, 500),
		reload: debounce(function () {
			this.isChanging = true;
			getArticles(
				this.postTypes,
				this.category,
				this.offset,
				this.highlightArticles,
				this.articlesPerPage + 1
			)
				.then((res) => {
					if (res) {
						if (typeof res != 'object') {
							res = JSON.parse(res);
						}
						this.offset = res.offset - 1;
						this.addArticles(res.data, true);
						currentFactBox = 0;

						// Set AOS data attributes on the first results
						this.$nextTick(() => {
							for (let i = 0; i < this.sortedArticles.length; i++) {
								if (i <= this.articlesPerPage) {
									this.$refs['item' + i][0].setAttribute('data-aos', 'fade-up');
									this.$refs['item' + i][0].setAttribute(
										'data-aos-anchor-placement',
										'center-bottom'
									);
								}
							}
						});
					}
				})
				.always(() => (this.isChanging = false));
		}, 500),
	},
	created() {
		this.reload();
	},
};

function getArticles(
	postTypes = '',
	category = 'all',
	offset = 0,
	highlightArticles = [],
	numberToFetch = 11
) {
	const params = {
		per_page: numberToFetch,
		offset: offset,
		lang: currentLanguage,
		_: '1.5',
	};

	if (postTypes !== '') {
		params['post_types'] = postTypes;
	}
	if (category !== 'all') {
		params['category'] = category;
	}

	// On first pass, add the prioritized articles
	/* if (offset === 0 && highlightArticles.length > 0) {
		return $.when(
			$.get('/wp-json/be/v1/article', $.extend({}, params, { include: highlightArticles, orderby: 'include' })),
			$.get('/wp-json/be/v1/article', $.extend({}, params, { exclude: highlightArticles })),
		).then((firstArticles, followingArticles) => [...firstArticles[0], ...followingArticles[0]]);
	} else {
		return $.get('/wp-json/be/v1/article', params);
	} */

	return $.get('/wp-json/be/v1/article', params);
}

function replaceUrlParameterValue(param, value, url) {
	const replaced = url.replace(new RegExp('[?&]' + param + '=[^&]+'), '');

	if (replaced.indexOf('?') === -1) {
		return `${replaced}?${param}=${value}`;
	} else {
		return `${replaced}&${param}=${value}`;
	}
}
